// @ts-strict-ignore
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTimelineEventsPagination } from '@/utils/hooks/useTimelineEventsPagination';
import { updateTimelineEventsFilters } from '../../actions/filters';
import { setEventsTimelineDrawerVisibility } from '../../actions/ui';
import { displayTextForReleaseStepType } from '../../helpers/displayHelpers';
import type { RootState } from '../../reducers/reducers';
import {
    selectApp,
    selectAppPlatform,
    selectAppStoreType,
    selectIsAppsDemoModeEnabled,
    selectIsStepTimelineDrawerVisible,
    selectIsWorkflowsCIStepEnabled,
    selectRelease,
} from '../../selectors/selectors';
import { type Release, ReleaseStepType, type TimelineEvent } from '../../types';
import { globalMessage } from '../../utils/globalMessage';
import { EventsTimeline, ReleaseTimelineSectionContext } from '../timelineEvents/EventsTimeline';
import { EventsTimelineDrawer } from '../timelineEvents/EventsTimelineDrawer';

export interface EventsTimelineSectionProps {
    events?: TimelineEvent[];
    maxItemsToDisplay?: number;
    orgId: string;
    appId: string;
    releaseId: string;
    releaseStepType?: ReleaseStepType;
    context: ReleaseTimelineSectionContext;
    isLoading?: boolean;
    stepRunId?: string;
    loadMore?: () => void;
    isLoadingMore?: boolean;
    hasMore?: boolean;
}

export const EventsTimelineSection: React.FC<EventsTimelineSectionProps> = ({
    events,
    maxItemsToDisplay,
    orgId,
    appId,
    releaseId,
    releaseStepType,
    isLoading,
    context,
    loadMore,
    isLoadingMore,
    hasMore,
    stepRunId,
}) => {
    const dispatch = useDispatch();

    const stepId = stepRunId ?? releaseStepType;
    const isDrawerVisible = useSelector((state: RootState) => selectIsStepTimelineDrawerVisible(state, stepId));

    const release = useSelector((state: RootState) => selectRelease(state, releaseId)) as Release;

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));

    return (
        <>
            <EventsTimeline
                isLoading={isLoading}
                events={events}
                maxItemsToDisplay={maxItemsToDisplay ?? 3}
                orgId={orgId}
                appId={appId}
                release={release}
                releaseType={release.type}
                shouldHideUpcomingEvents={context === ReleaseTimelineSectionContext.AppHome}
                releaseStepType={releaseStepType}
                context={context}
            />
            <EventsTimelineDrawer
                events={events}
                open={isDrawerVisible}
                onClose={() => {
                    dispatch(setEventsTimelineDrawerVisibility(null, false));
                    dispatch(updateTimelineEventsFilters({ filters: [] }));
                }}
                orgId={orgId}
                appId={appId}
                version={release.releaseId}
                releaseStepDisplayName={displayTextForReleaseStepType(
                    releaseStepType,
                    platform,
                    appStoreType,
                    isWorkflowsCIStepEnabled,
                )}
                releaseStepType={releaseStepType}
                loadMore={loadMore}
                isLoading={isLoadingMore || isLoading}
                hasMore={hasMore}
            />
        </>
    );
};

type ReleaseEventsTimelineSectionProps = Omit<EventsTimelineSectionProps, 'events' | 'isLoading'>;

export const ReleaseEventsTimelineSection: React.FC<ReleaseEventsTimelineSectionProps> = (props) => {
    const { appId, releaseId, orgId, stepRunId, releaseStepType } = props;

    const workflowRunId = useSelector((state: RootState) => {
        const release = selectRelease(state, releaseId);
        return release.workflowRunId;
    });

    const workflowId = useSelector((state: RootState) => {
        const app = selectApp(state, appId);
        return app.workflowId;
    });

    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const paginationResult = useTimelineEventsPagination({
        orgId,
        appId,
        releaseId,
        releaseStepType,
        workflowId,
        workflowRunId,
        stepRunIds: stepRunId ? [stepRunId] : [],
        initialLoad: true,
    });

    const { events, isLoading, error, loadMore, isLoadingMore, paginationInfo, refresh } = paginationResult;

    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    React.useEffect(() => {
        if (error) {
            globalMessage.error('An error occurred when fetching release timeline events.');
        }
    }, [error]);

    React.useEffect(() => {
        if (release?.RenderedAt && !isViewingAppsInDemoMode) {
            refresh();
        }
    }, [release?.RenderedAt]);

    return (
        <EventsTimelineSection
            {...props}
            loadMore={loadMore}
            isLoadingMore={isLoadingMore}
            hasMore={paginationInfo?.hasMore}
            events={events || []}
            isLoading={isLoading}
            key={`${releaseId}-${stepRunId}-${releaseStepType}`}
        />
    );
};
